import { printViaIframe } from './printHelper';
import {
    getStandMagnumFacts, formatStandMagnumLine, getStandIpscFacts, formatIpscStageLine,
} from './conventionCourse';

const escapeHtml = (s = '') => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export function getStandDisplayValues(entry = {}) {
    // Format legacy distance notes for display without changing the saved text.
    const notes = (entry.notes || '').toString().trim()
        .replace(/\bavstand\s*:?\s*(?=\d)/gi, 'Avstand er ');
    const toPositive = (value) => {
        const n = parseInt(value, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    };
    return {
        shootingTime: toPositive(entry.shootingTime),
        shootingPosition: (entry.shootingPosition || '').toString().trim() || null,
        notes: notes || null,
        scoreHits: toPositive(entry.scoreHits),
        scoreTargets: toPositive(entry.scoreTargets),
        scoreGroups: toPositive(entry.scoreGroups),
        scoreInner: toPositive(entry.scoreInner),
    };
}

export function printStandPoster({ convention = {}, stand }) {
    const standNum = parseInt(stand, 10) || 1;
    const entry = (convention?.stageStands || []).find(item => parseInt(item.stand, 10) === standNum) || {};
    const {
        shootingTime, shootingPosition, notes, scoreTargets, scoreGroups, scoreInner,
    } = getStandDisplayValues(entry);
    const standImage = convention?.stageStandImages
        ? convention.stageStandImages[standNum - 1]
        : null;
    const standImageLink = standImage
        ? (standImage.s3LargeLink || standImage.s3MediumLink || standImage.s3Link || '')
        : '';

    const groups = scoreGroups != null ? scoreGroups : 1;
    const factRows = [];
    const ipscFacts = getStandIpscFacts(entry);
    if (ipscFacts.ipsc) {
        // IPSC (dynamisk): the stage definition replaces the NSF mål/målgrupper/skytetid facts.
        factRows.push({ value: ipscFacts.ipsc.paper, label: 'PAPER' });
        factRows.push({ value: ipscFacts.ipsc.poppers + ipscFacts.ipsc.plates, label: 'STEEL' });
        factRows.push({ value: ipscFacts.ipscMax, label: 'POINTS' });
    }
    if (!ipscFacts.ipsc && scoreTargets != null) {
        factRows.push({ value: scoreTargets, label: 'MÅL' });
    }
    if (!ipscFacts.ipsc && scoreGroups != null) {
        factRows.push({ value: scoreGroups, label: `MÅLGRUPPE${groups > 1 ? 'R' : ''}` });
    }
    if (!ipscFacts.ipsc && shootingTime != null) {
        factRows.push({ value: shootingTime, label: 'SEKUNDER SKYTETID' });
    }
    const factsHtml = factRows.map(row => `
        <div class="fact">
            <span class="fact-value">${escapeHtml(String(row.value))}</span>
            <span class="fact-label">${escapeHtml(row.label)}</span>
        </div>
    `).join('');

    const extraLines = [];
    const ipscLine = formatIpscStageLine(ipscFacts);
    if (ipscLine) extraLines.push(ipscLine);
    if (ipscFacts.ipscBrief) extraLines.push(ipscFacts.ipscBrief.toUpperCase());
    if (shootingPosition) extraLines.push(shootingPosition.toUpperCase());
    if (notes) extraLines.push(notes.toUpperCase());
    if (scoreInner != null) extraLines.push('SONER TELLES');
    // Magnum-specific position/time set on the stand, e.g. "MAGNUM: STÅENDE 2 HENDER, 18 SEKUNDER".
    const magnumLine = formatStandMagnumLine(getStandMagnumFacts(entry));
    if (magnumLine) extraLines.push(magnumLine);
    const extraHtml = extraLines.map(line => `<div class="extra-line">${escapeHtml(line)}</div>`).join('');

    const imageHtml = standImageLink
        ? `<div class="poster-image"><img src="${escapeHtml(standImageLink)}" alt="Standplass ${standNum}" /></div>`
        : '';

    const html = `
        <div class="print-section standplass-poster">
            <div class="poster-convention">${escapeHtml(convention?.title || '')}</div>
            <div class="poster-stand-label">STANDPLASS</div>
            <div class="poster-stand-number">${standNum}</div>
            <div class="poster-facts">${factsHtml}</div>
            <div class="poster-extra">${extraHtml}</div>
            ${imageHtml}
        </div>
    `;
    // ONE A4 page, guaranteed: the poster is a fixed-height flex column sized
    // inside the printable area (printViaIframe's @page has fixed 10mm margins
    // plus 5mm .print-section padding → 277mm printable, we use 260mm for slack).
    // The photo gets `flex: 1` so it absorbs exactly the height the text leaves
    // over and can never push content onto a second sheet.
    const customCss = `
        .standplass-poster {
            font-family: Arial, Helvetica, sans-serif; color: #000; text-align: center;
            box-sizing: border-box; height: 260mm; overflow: hidden;
            display: flex; flex-direction: column; align-items: center;
            page-break-inside: avoid; break-inside: avoid;
        }
        .standplass-poster .poster-convention { font-size: 16pt; margin-bottom: 4mm; }
        .standplass-poster .poster-stand-label { font-size: 30pt; font-weight: 700; letter-spacing: 2mm; }
        .standplass-poster .poster-stand-number {
            font-size: ${standImageLink ? '90pt' : '130pt'};
            font-weight: 700;
            line-height: 1;
            margin-bottom: ${standImageLink ? '5mm' : '8mm'};
        }
        .standplass-poster .poster-facts {
            display: flex;
            flex-direction: column;
            gap: ${standImageLink ? '3mm' : '6mm'};
            margin-bottom: ${standImageLink ? '6mm' : '10mm'};
        }
        .standplass-poster .fact { display: flex; align-items: baseline; justify-content: center; gap: 5mm; }
        .standplass-poster .fact-value { font-size: ${standImageLink ? '40pt' : '48pt'}; font-weight: 700; }
        .standplass-poster .fact-label { font-size: ${standImageLink ? '22pt' : '26pt'}; font-weight: 700; }
        .standplass-poster .extra-line {
            font-size: ${standImageLink ? '18pt' : '22pt'};
            font-weight: 700;
            margin-bottom: 3mm;
        }
        .standplass-poster .poster-image {
            flex: 1 1 auto; min-height: 0; width: 100%; margin-top: 4mm;
            display: flex; align-items: center; justify-content: center;
        }
        .standplass-poster .poster-image img { max-width: 100%; max-height: 100%; object-fit: contain; }
        @media print {
            @page { size: A4 portrait; }
        }
    `;
    printViaIframe(html, { customCss, waitForImages: true });
}
